import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CitationEntry } from '@/lib/types/search-results';
import type { ClaimsResponse } from '@/lib/citations/claims';

export interface DocumentContent {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  contentType: 'html' | 'text' | 'markdown';
  source: 'pisrs' | 'eurlex' | 'sodnapraksa' | 'usrs' | 'hudoc' | 'other';
  sourceUrl?: string;
  metadata?: {
    pisrsId?: string;
    celex?: string;
    caseId?: string;
    date?: string;
    status?: string;
    relatedLaws?: string[];
    // USRS specific
    caseNumber?: string;
    decisionType?: string;
    // EUR-Lex specific
    type?: string; // direktiva, uredba, sklep, sodba
    euArticle?: string; // Article number for ELI scroll-to (e.g., "32", "14a")
  };
}

// History entry preserves full panel state (mode + document + canvas)
export interface HistoryEntry {
  mode: 'view' | 'canvas' | 'claims';
  document: DocumentContent | null;
  canvasDocId: string | null;
}

interface DocumentViewerState {
  // Panel visibility
  isOpen: boolean;

  // Current document
  document: DocumentContent | null;

  // History of opened documents (back stack) — includes mode + canvas state
  history: HistoryEntry[];

  // Forward stack (for redo navigation)
  forwardStack: HistoryEntry[];

  // Panel size (percentage)
  panelSize: number;

  // Citation highlight state
  highlightSnippet: string | null;
  highlightCitationId: number | null;

  // Pending question for chat input (cross-component communication)
  pendingDocumentQuestion: string | null;

  // Panel mode: document view, canvas editor, or claims & evidence
  mode: 'view' | 'canvas' | 'claims';
  activeCanvasDocId: string | null;

  // Claims & Evidence data (set from ChatInterface when claims arrive)
  activeClaims: ClaimsResponse | null;
  activeClaimsCitationMap: CitationEntry[] | null;
  claimsCitationHandler: ((citation: CitationEntry) => void) | null;

  // Pending fragment to insert into canvas (consumed by CanvasEditor)
  pendingFragment: { html: string; sourceType: string; sourceLabel: string } | null;

  // Focus mode: when true, AI obravnava vsak query kot follow-up o odprtem dokumentu.
  // Auto-aktivira se ob odpiranju ne-PISRS dokumenta (USRS/EUR-Lex/SP/ESČP/e-KURS) —
  // tipično si pravnik takrat želi pogovor o tem dokumentu. PISRS akti so primarni
  // iskalni cilj (pri njih ostaja iskanje sorodnih aktov/posegov US smiselno), zato off.
  // Resetira se ob zaprtju panela; uporabnik ga lahko kadarkoli izklopi (gumb Reset).
  focusMode: boolean;

  // Actions
  openDocument: (doc: DocumentContent) => void;
  openDocumentWithHighlight: (doc: DocumentContent, snippet: string, citationId: number) => void;
  updateDocumentContent: (partial: Partial<DocumentContent>) => void;
  updateDocumentWithHighlight: (partial: Partial<DocumentContent>, snippet: string, citationId: number) => void;
  closePanel: () => void;
  togglePanel: () => void;
  setPanelSize: (size: number) => void;
  goBack: () => void;
  goForward: () => void;
  clearHistory: () => void;
  clearHighlight: () => void;
  setPendingDocumentQuestion: (q: string | null) => void;
  setMode: (mode: 'view' | 'canvas' | 'claims') => void;
  setActiveCanvasDocId: (id: string | null) => void;
  setClaims: (claims: ClaimsResponse | null, citationMap: CitationEntry[] | null) => void;
  setClaimsCitationHandler: (handler: ((citation: CitationEntry) => void) | null) => void;
  setPendingFragment: (fragment: { html: string; sourceType: string; sourceLabel: string } | null) => void;
  setFocusMode: (enabled: boolean) => void;
}

// ---- Persist helperji (TODO #15) ----
//
// Strategija: MAJHNE dokumente (USRS odločbe ~15 KB, e-KURS komentarji, NS
// robne, sodna praksa) persistamo Z VSEBINO — zanje zanesljiv re-fetch ni
// vedno mogoč (e-KURS/NS imata source 'other' brez metapodatkov, USRS skupne
// opravilne številke na us-rs.si vrnejo 404). VELIKI dokumenti (PISRS zakoni,
// EUR-Lex konsolidirani akti do 27 MB) se shranijo brez vsebine in se ob
// mountu ponovno naložijo (refetch effect v DocumentViewer).

/** Vsebina do te velikosti se persista; večja se ob mountu ponovno fetcha. */
const PERSIST_CONTENT_MAX = 100_000;
/** Runtime omejitev history (RAM — vnosi nosijo polne vsebine). */
const MAX_HISTORY = 30;
/** Največ toliko zadnjih history vnosov gre v localStorage (najstarejši se
 *  obrežejo; ob kvoti viewerQuotaSafeStorage degradira vsebine → zgodovino). */
const PERSIST_HISTORY_MAX = MAX_HISTORY;

/**
 * Marker za prehodne placeholderje (»Nalagam vsebino…«, fallback sporočila).
 * Vsebina z markerjem se NIKOLI ne persista — po reloadu se tako sproži
 * ponovni fetch namesto zamrznjenega placeholderja.
 */
export const VIEWER_PLACEHOLDER_MARKER = 'data-viewer-placeholder';

function stripDocContent(doc: DocumentContent | null): DocumentContent | null {
  if (!doc) return null;
  if (
    doc.content.length <= PERSIST_CONTENT_MAX &&
    !doc.content.includes(VIEWER_PLACEHOLDER_MARKER)
  ) {
    return doc;
  }
  return { ...doc, content: '' };
}

function stripEntryContent(entry: HistoryEntry): HistoryEntry {
  return { ...entry, document: stripDocContent(entry.document) };
}

function isQuotaExceededError(e: unknown): boolean {
  return (
    e instanceof DOMException &&
    (e.name === 'QuotaExceededError' ||
      e.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
      e.code === 22 ||
      e.code === 1014)
  );
}

/**
 * Kvota-varna shramba: ob QuotaExceeded najprej odvrže vse vsebine (ostanejo
 * metapodatki → refetch pot), nato še zgodovino. Persist write ne sme nikoli
 * tiho pasti (nauk iz canvas kvota buga, seja 204).
 */
const viewerQuotaSafeStorage = {
  getItem: (name: string) =>
    typeof localStorage !== 'undefined' ? localStorage.getItem(name) : null,
  removeItem: (name: string) => {
    if (typeof localStorage !== 'undefined') localStorage.removeItem(name);
  },
  setItem: (name: string, value: string) => {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(name, value);
      return;
    } catch (e) {
      if (!isQuotaExceededError(e)) {
        console.warn('[viewer-store] localStorage.setItem napaka (ne-kvota):', e);
        return;
      }
    }
    try {
      const env = JSON.parse(value) as {
        state?: {
          document?: DocumentContent | null;
          history?: HistoryEntry[];
          forwardStack?: HistoryEntry[];
        };
      };
      const s = env.state;
      if (s) {
        if (s.document) s.document = { ...s.document, content: '' };
        s.history = (s.history || []).map((h) => ({
          ...h,
          document: h.document ? { ...h.document, content: '' } : null,
        }));
        s.forwardStack = [];
      }
      try {
        localStorage.setItem(name, JSON.stringify(env));
        console.warn('[viewer-store] kvota presežena — vsebine odvržene, metapodatki ohranjeni');
        return;
      } catch {
        if (s) {
          s.history = [];
          localStorage.setItem(name, JSON.stringify(env));
          console.warn('[viewer-store] kvota presežena — tudi zgodovina odvržena');
        }
      }
    } catch (e2) {
      console.error('[viewer-store] shranjevanje preskočeno (kvota + retry fail):', e2);
    }
  },
};

export const useDocumentViewerStore = create<DocumentViewerState>()(
  persist(
    (set, get) => ({
  isOpen: false,
  document: null,
  history: [],
  forwardStack: [],
  panelSize: 30,
  highlightSnippet: null,
  highlightCitationId: null,
  pendingDocumentQuestion: null,
  mode: 'view',
  activeCanvasDocId: null,
  activeClaims: null,
  activeClaimsCitationMap: null,
  claimsCitationHandler: null,
  pendingFragment: null,
  focusMode: false,

  openDocument: (doc) => {
    const { document: current, mode, activeCanvasDocId, history, forwardStack } = get();
    // If same document is already open, just update metadata (e.g., euArticle for scroll-to)
    if (current && current.id === doc.id && mode === 'view') {
      set({ document: doc, highlightSnippet: null, highlightCitationId: null });
      return;
    }
    // Save current state (including canvas) to history before navigating.
    // TODO #15: forwardStack se NE zavrže (browser semantika je pravniku
    // brisala dokumente — »ostalo samo 5«), ampak linearizira v history:
    // nazaj-veriga po odprtju D je D → F1 → F2 → … → starejši.
    const hasCurrentState = current || mode === 'canvas';
    const entry: HistoryEntry = { mode, document: current, canvasDocId: activeCanvasDocId };
    const linearized = hasCurrentState
      ? [...history, ...forwardStack, entry]
      : [...history, ...forwardStack];
    set({
      isOpen: true,
      document: doc,
      history: linearized.slice(-MAX_HISTORY),
      forwardStack: [],
      highlightSnippet: null,
      highlightCitationId: null,
      mode: 'view',
      focusMode: doc.source !== 'pisrs',
    });
  },

  openDocumentWithHighlight: (doc, snippet, citationId) => {
    const { document: current, mode, activeCanvasDocId, history, forwardStack } = get();
    const hasCurrentState = current || mode === 'canvas';
    const entry: HistoryEntry = { mode, document: current, canvasDocId: activeCanvasDocId };
    const linearized = hasCurrentState
      ? [...history, ...forwardStack, entry]
      : [...history, ...forwardStack];
    set({
      isOpen: true,
      document: doc,
      history: linearized.slice(-MAX_HISTORY),
      forwardStack: [],
      highlightSnippet: snippet,
      highlightCitationId: citationId,
      mode: 'view',
      focusMode: doc.source !== 'pisrs',
    });
  },

  updateDocumentContent: (partial) => {
    const current = get().document;
    if (!current) return;
    set({ document: { ...current, ...partial } });
  },

  updateDocumentWithHighlight: (partial, snippet, citationId) => {
    const current = get().document;
    if (!current) return;
    set({
      document: { ...current, ...partial },
      highlightSnippet: snippet,
      highlightCitationId: citationId,
    });
  },

  closePanel: () => {
    set({ isOpen: false, focusMode: false });
  },

  togglePanel: () => {
    set({ isOpen: !get().isOpen });
  },

  setPanelSize: (size) => {
    set({ panelSize: size });
  },

  goBack: () => {
    const { history, document, mode, activeCanvasDocId, forwardStack } = get();
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    const currentEntry: HistoryEntry = { mode, document, canvasDocId: activeCanvasDocId };
    set({
      document: previous.document,
      mode: previous.mode,
      activeCanvasDocId: previous.canvasDocId,
      history: history.slice(0, -1),
      forwardStack: [...forwardStack, currentEntry],
      highlightSnippet: null,
      highlightCitationId: null,
      focusMode: previous.document ? previous.document.source !== 'pisrs' : false,
    });
  },

  goForward: () => {
    const { forwardStack, document, mode, activeCanvasDocId, history } = get();
    if (forwardStack.length === 0) return;
    const next = forwardStack[forwardStack.length - 1];
    const currentEntry: HistoryEntry = { mode, document, canvasDocId: activeCanvasDocId };
    set({
      document: next.document,
      mode: next.mode,
      activeCanvasDocId: next.canvasDocId,
      forwardStack: forwardStack.slice(0, -1),
      history: [...history, currentEntry],
      highlightSnippet: null,
      highlightCitationId: null,
      focusMode: next.document ? next.document.source !== 'pisrs' : false,
    });
  },

  clearHistory: () => {
    set({ history: [], forwardStack: [] });
  },

  clearHighlight: () => {
    set({ highlightSnippet: null, highlightCitationId: null });
  },

  setPendingDocumentQuestion: (q) => {
    set({ pendingDocumentQuestion: q });
  },

  setMode: (mode) => {
    set({
      mode,
      // Canvas nima claims — počisti zastarele da se ne prikazujejo napačne
      ...(mode === 'canvas' ? { activeClaims: null, activeClaimsCitationMap: null } : {}),
    });
  },

  setActiveCanvasDocId: (id) => {
    set({ activeCanvasDocId: id });
  },

  setClaims: (claims, citationMap) => {
    set({ activeClaims: claims, activeClaimsCitationMap: citationMap });
  },

  setClaimsCitationHandler: (handler) => {
    set({ claimsCitationHandler: handler });
  },

  setPendingFragment: (fragment) => {
    set({ pendingFragment: fragment });
  },

  setFocusMode: (enabled) => {
    set({ focusMode: enabled });
  },
    }),
    {
      name: 'tribonianus-document-viewer',
      storage: createJSONStorage(() => viewerQuotaSafeStorage),
      partialize: (state) => ({
        isOpen: state.isOpen,
        panelSize: state.panelSize,
        mode: state.mode,
        activeCanvasDocId: state.activeCanvasDocId,
        // TODO #15: majhni dokumenti z vsebino, veliki samo metapodatki —
        // zanje DocumentViewer refetch effect vsebino ponovno naloži ob mountu.
        document: stripDocContent(state.document),
        // Tudi history/forwardStack (puščice ← →) preživita reload; goBack na
        // vnos s content === '' sproži isti refetch effect.
        history: state.history.slice(-PERSIST_HISTORY_MAX).map(stripEntryContent),
        forwardStack: state.forwardStack.slice(-PERSIST_HISTORY_MAX).map(stripEntryContent),
      }),
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<DocumentViewerState>) };
        // claims podatki niso persistirani — claims mode po reloadu nima vira
        if (merged.mode === 'claims') merged.mode = 'view';
        // Legacy persisted stanje (pred persistom dokumenta): odprt 'view' brez
        // dokumenta bi pustil prazen pane — raje zapri.
        if (merged.isOpen && merged.mode !== 'canvas' && !merged.document) {
          merged.isOpen = false;
        }
        return merged;
      },
    }
  )
);
